// src/components/manuscripts/ManuscriptSection.jsx
//
// One titled group of manuscript cards -- In Progress, Collaboration, Needs
// Revision -- in either of its two shapes.
//
// Collapsed (the default) it is a single horizontally scrolling rail, which is
// what the prototype draws: one row per section, however many manuscripts are
// in it. Expanded it becomes the wrapping grid, so "show me all of them" is one
// click rather than a scroll of unknown length. The prototype's "View All" link
// is that control.
//
// The rail is NOT the arrow-scroller this tab used to have. That one always
// rendered both arrows whether or not there was anything to scroll to, and had
// no way to see a section whole.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { QUIET_BUTTON } from './manuscriptStyles';
import '../../styles/ManuscriptWorkspace.css';

/**
 * How far one arrow press moves the rail.
 *
 * A card plus its gap, so a press lands cards on the same edge it found them
 * rather than leaving one sliced in half. 240 is the card's flex-basis and 12
 * is --rv-space-md; they are duplicated here because a scroll distance has to
 * be a number and CSS custom properties are not readable as layout values
 * without a getComputedStyle round trip per press.
 */
const STEP = 252;

export default function ManuscriptSection({
  id,
  title,
  items,
  modifier = '',
  showCount = false,
  children,
}) {
  const railRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    // 1px of slack: sub-pixel layout makes scrollWidth exceed clientWidth by a
    // fraction on rails that are not actually scrollable, which would leave a
    // dead arrow permanently lit.
    const max = el.scrollWidth - el.clientWidth;
    setOverflow({ left: el.scrollLeft > 1, right: el.scrollLeft < max - 1 });
  }, []);

  useEffect(() => {
    if (expanded) {
      setOverflow({ left: false, right: false });
      return undefined;
    }
    const el = railRef.current;
    if (!el) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // The children matter as much as the box: a card whose cover image loads
    // late changes scrollWidth without resizing the rail itself.
    for (const child of el.children) ro.observe(child);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [expanded, measure, items.length]);

  if (!items.length) return null;

  const scrollBy = (dir) =>
    railRef.current?.scrollBy({ left: dir * STEP, behavior: 'smooth' });

  // Offer the expander when there is something the rail is not showing, and
  // keep offering it once expanded so there is a way back.
  const canExpand = expanded || overflow.right || overflow.left;

  return (
    <section
      data-testid={`manuscripts-section-${id}`}
      className={`rv-manuscripts__section${modifier ? ` rv-manuscripts__section--${modifier}` : ''}`}
    >
      <div className="rv-manuscripts__section-head">
        <h2 className="rv-manuscripts__section-title">
          {title}
          {showCount && <span className="rv-manuscripts__section-count">{items.length}</span>}
        </h2>
        {canExpand && (
          <Button
            size="small"
            data-testid={`manuscripts-expand-${id}`}
            onClick={() => setExpanded((v) => !v)}
            sx={{ ...QUIET_BUTTON, minHeight: 'auto', py: 0.25, fontWeight: 700 }}
          >
            {expanded ? 'Show less' : `View all (${items.length})`}
          </Button>
        )}
      </div>

      {expanded ? (
        <div className="rv-manuscripts__cards">{children}</div>
      ) : (
        <div className="rv-manuscripts__rail-wrap">
          <IconButton
            size="small"
            aria-label={`Scroll ${title} left`}
            className="rv-manuscripts__rail-arrow rv-manuscripts__rail-arrow--left"
            disabled={!overflow.left}
            onClick={() => scrollBy(-1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          <div className="rv-manuscripts__rail" ref={railRef}>
            {children}
          </div>

          <IconButton
            size="small"
            aria-label={`Scroll ${title} right`}
            className="rv-manuscripts__rail-arrow rv-manuscripts__rail-arrow--right"
            disabled={!overflow.right}
            onClick={() => scrollBy(1)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
      )}
    </section>
  );
}
